import { FinancingInput } from '@oneaudi/onegraph-client';
import { createContext, useEffect, useMemo, useState } from 'react';
import { AemMarketConfig, MarketIdentifier } from '~/app/market-config';
import { loadMarketConfig } from '~/app/market-config/load-market-config';
import { MarketFormatter } from '~/app/market-config/market-formatter';
import { CarConfiMarketConfiguration } from '~/app/market-config/models/car-confi-market-configuration';
import { NgcCarIdentifier } from '~/app/utils/graphql-sub-types';
import IsProdEnvironment from '~/app/utils/is-production-environment';
import { removeTypenameFields } from '~/graphql/utils';

type MarketConfigContextType = {
	market: MarketIdentifier;
	language: string;
	config: CarConfiMarketConfiguration;
	setFinancingParameter: (financingInput: FinancingInput | null) => void;
	/** Provides utility methods for formatting values according to market preferences. */
	formatter: MarketFormatter;
	numberFormatter: Intl.NumberFormat;
};

export const MarketContext = createContext({} as MarketConfigContextType);

export function MarketConfigProvider({
	aemConfig,
	initialCarIdentifier,
	initialFinancialCalculatorSettings,
	children,
}: {
	aemConfig: AemMarketConfig;
	initialCarIdentifier: NgcCarIdentifier;
	initialFinancialCalculatorSettings?: FinancingInput;
	children: React.ReactNode;
}) {
	const market = aemConfig.market.toUpperCase() as MarketIdentifier;
	const config = loadMarketConfig(aemConfig);

	// Memoize the base market config to prevent recreation on every render
	const baseMarketConfig = useMemo(() => {
		return {
			...config,
			carIdentifier: removeTypenameFields(initialCarIdentifier),
		};
	}, [aemConfig, initialCarIdentifier]);

	const [marketConfig, setMarketConfig] = useState(baseMarketConfig);

	// Update market config when base config or financing settings change
	useEffect(() => {
		setMarketConfig({
			...baseMarketConfig,
			...(initialFinancialCalculatorSettings && { financingInput: initialFinancialCalculatorSettings }),
		});
	}, [baseMarketConfig, initialFinancialCalculatorSettings]);

	// Memoize the context value to prevent unnecessary re-renders
	const value = useMemo((): MarketConfigContextType => {
		return {
			market,
			language: aemConfig.language.toLowerCase(),
			config: marketConfig,
			formatter: marketConfig.getFormatter(aemConfig),
			numberFormatter: new Intl.NumberFormat(`${market}-${aemConfig.language}`),
			setFinancingParameter: (financingInput: FinancingInput | null) => {
				setMarketConfig((previous: CarConfiMarketConfiguration) => ({ ...previous, financingInput }));
			},
		};
	}, [aemConfig, market, marketConfig]);

	if (!IsProdEnvironment()) {
		console.info('New NBAs: ', marketConfig.nextBestActions);
		console.info('New Primary CTA: ', marketConfig.summaryFooterCta);
		console.info('New additionalPrimaryNba: ', marketConfig.additionalPrimaryNba);
	}

	return <MarketContext.Provider value={value}>{children}</MarketContext.Provider>;
}
